import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { socketService } from "../services/socketService";
import { openSnackbar } from "api/snackbar";
import { getStore } from "store/reducers";
import useAuth from "hooks/useAuth";
import { canAccessAdminPanel } from "utils/adminAccess";
import NotificationToast from "components/NotificationToast";

// Create Notification Context
const NotificationContext = createContext();

// Local storage keys for notification persistence
const ADMIN_UNREAD_COUNT_KEY = "admin_unread_count";
const ADMIN_REPORT_COUNT_KEY = "admin_report_count";
const ADMIN_COMMENT_COUNT_KEY = "admin_comment_count";

export const NotificationProvider = ({ children }) => {
  // State
  const [notifications, setNotifications] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const [adminReportCount, setAdminReportCount] = useState(0);
  const [adminCommentCount, setAdminCommentCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tokenExpiryTime, setTokenExpiryTime] = useState(0);
  // State for toast notification
  const [toastNotification, setToastNotification] = useState(null);
  // State to track if we should play sounds (only for new notifications via WebSocket)
  const [shouldPlaySound, setShouldPlaySound] = useState(false);

  const { user } = useAuth();
  // use custom snackbar helper
  const store = getStore();
  const { accessToken } = store.getState().auth;

  // Setup socket handlers
  const setupSocketHandlers = useCallback(() => {
    socketService.setHandlers({
      onConnect: () => {
        setIsConnected(true);
      },

      onDisconnect: () => {
        setIsConnected(false);
      },

      onTokenExpiry: (expiresIn) => {
        setTokenExpiryTime(expiresIn);
      },

      onUnreadCount: (count) => {
        setUnreadCount(count);
      },

      onNotificationsList: (notificationsList) => {
        // This is initial data load or refresh - don't play sound
        setNotifications(notificationsList);
        setShouldPlaySound(false);
      },

      onNewNotification: (notification, count) => {
        if (notification) {
          setNotifications((prev) => [notification, ...prev]);
          // This is a genuinely new notification via WebSocket - allow sound
          setShouldPlaySound(true);

          // Show toast notification for new notifications (custom snackbar)
          openSnackbar({
            open: true,
            message: `${notification.title} — ${(
              notification.message || ""
            ).substring(0, 60)}`,
            variant: "alert",
            alert: { color: "info" },
            autoHideDuration: 4000,
          });
        }
        if (count !== undefined) {
          setUnreadCount(count);
        }
      },

      onReadStatusUpdate: (notificationId, count) => {
        // Update notification read status
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? { ...notification, is_read: true }
              : notification,
          ),
        );

        if (count !== undefined) {
          setUnreadCount(count);
        }
      },

      onAllReadStatusUpdate: (count) => {
        // Mark all notifications as read
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, is_read: true })),
        );

        setUnreadCount(0);
      },

      onError: (message) => {
        console.error("Socket error:", message);
        // Handle error display to user
        openSnackbar({
          open: true,
          message: `Erreur de connexion: ${message}`,
          variant: "alert",
          alert: { color: "error" },
          autoHideDuration: 3000,
        });
      },
    });

    // Admin handlers
    socketService.setAdminHandlers({
      onConnect: () => {
        setIsAdmin(true);
      },

      onDisconnect: () => {
        setIsAdmin(false);
      },

      onMessage: (data) => {
        // Handle admin-specific notifications
        // Handle initial counts from server
        if (data.type === "initial_counts") {

          // Update report and comment counts with exact numbers from server
          if (data.report_count !== undefined) {
            setAdminReportCount(data.report_count);
            localStorage.setItem(ADMIN_REPORT_COUNT_KEY, data.report_count);
          }

          if (data.comment_count !== undefined) {
            setAdminCommentCount(data.comment_count);
            localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, data.comment_count);
          }

          // Set combined unread count for badge using exact values
          if (
            data.report_count !== undefined &&
            data.comment_count !== undefined
          ) {
            const totalUnread = data.report_count + data.comment_count;
            setAdminUnreadCount(totalUnread);
            localStorage.setItem(ADMIN_UNREAD_COUNT_KEY, totalUnread);
          }

          return;
        }

        // Handle full notifications list when requested (panel open)
        if (data.type === "notifications_list") {
          const list = Array.isArray(data.notifications)
            ? data.notifications
            : [];
          setAdminNotifications(list);
          // Optionally update unread badge to number of items
          setAdminUnreadCount((prev) =>
            typeof data.unread_count === "number"
              ? data.unread_count
              : list.length,
          );
          return;
        }

        // Handle counts update acknowledgements
        if (data.type === "counts_update") {
          if (typeof data.report_count === "number") {
            setAdminReportCount(data.report_count);
            localStorage.setItem(ADMIN_REPORT_COUNT_KEY, data.report_count);
          }
          if (typeof data.comment_count === "number") {
            setAdminCommentCount(data.comment_count);
            localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, data.comment_count);
          }
          if (
            typeof data.report_count === "number" &&
            typeof data.comment_count === "number"
          ) {
            const totalUnread = data.report_count + data.comment_count;
            setAdminUnreadCount(totalUnread);
            localStorage.setItem(ADMIN_UNREAD_COUNT_KEY, totalUnread);
          }
          return;
        }

        if (data.type === "new_notification") {
          // Add the new admin notification
          if (data.notification) {
            setAdminNotifications((prev) => [data.notification, ...prev]);
            // This is a genuinely new admin notification via WebSocket - allow sound
            setShouldPlaySound(true);

            // Show toast notification for the admin
            setToastNotification(data.notification);

            // Update admin unread count
            if (data.unread_count !== undefined) {
              setAdminUnreadCount(data.unread_count);
            } else {
              setAdminUnreadCount((prev) => prev + 1);
            }
          }
        } else if (data.type === "new_report") {
          // Process report notifications
          if (data.report) {
            // Create a notification object from the report data
            const reportNotification = {
              id: data.report.id,
              title: `${data.report.report_type}`,
              message: `Un contenu a été signalé: "${data.report.content_object?.title || "Sans titre"}"`,
              notification_type: "REPORT",
              is_read: false,
              created_at: data.report.created_at,
              data: {
                report_id: data.report.id,
                report_type: data.report.report_type,
                content_type: data.report.content_type,
                content_id: data.report.content_object?.id,
                content_object: data.report.content_object,
                reporter: data.report.reporter,
                description: data.report.description,
              },
            };

            // Add to admin notifications
            setAdminNotifications((prev) => [reportNotification, ...prev]);
            // This is a genuinely new report notification via WebSocket - allow sound
            setShouldPlaySound(true);

            // Show toast notification for the admin
            setToastNotification(reportNotification);

            // Update admin report count
            if (data.report_count !== undefined) {
              setAdminReportCount(data.report_count);
              localStorage.setItem(ADMIN_REPORT_COUNT_KEY, data.report_count);
            } else {
              const newCount = adminReportCount + 1;
              setAdminReportCount(newCount);
              localStorage.setItem(ADMIN_REPORT_COUNT_KEY, newCount);
            }
          }
        } else if (data.type === "new_comment") {
          // Process comment notifications
          if (data.comment) {
            // Create a notification object from the comment data
            const commentNotification = {
              id: data.comment.id,
              title: `${data.comment.content?.substring(0, 30)}...`,
              message: `Une nouvelle contribution requiert votre modération`,
              notification_type: "COMMENT",
              is_read: false,
              created_at: data.comment.created_at || new Date().toISOString(),
              data: {
                comment_id: data.comment.id,
                content_type: "comment",
                content: data.comment.content,
                content_object: data.comment.content_object,
                user: data.comment.user,
                author_id: data.comment.user?.id,
                author_name: data.comment.user?.full_name,
              },
            };

            // Add to admin notifications
            setAdminNotifications((prev) => [commentNotification, ...prev]);
            // This is a genuinely new comment notification via WebSocket - allow sound
            setShouldPlaySound(true);

            // Show toast notification for the admin
            setToastNotification(commentNotification);

            // Update admin comment count
            if (data.comment_count !== undefined) {
              setAdminCommentCount(data.comment_count);
              localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, data.comment_count);
            } else {
              const newCount = adminCommentCount + 1;
              setAdminCommentCount(newCount);
              localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, newCount);
            }

            // Update total unread count
            setAdminUnreadCount((prev) => prev + 1);
            localStorage.setItem(ADMIN_UNREAD_COUNT_KEY, adminUnreadCount + 1);
          }
        }
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependencies to prevent infinite loop

  // Initialize WebSocket connection
  useEffect(() => {
    if (user) {
      setIsAdmin(canAccessAdminPanel(user));
    }
    if (accessToken) {
      // Setup socket handlers
      setupSocketHandlers();

      // Connect to WebSocket
      socketService.connect(accessToken);

      // Cleanup on unmount
      return () => {
        socketService.disconnect();
      };
    }
  }, [accessToken, user, setupSocketHandlers]);

  // Reset toast notification
  const clearToastNotification = useCallback(() => {
    setToastNotification(null);
  }, []);

  // Function to mark notification as read
  const markAsRead = useCallback((notificationId) => {
    socketService.markAsRead(notificationId);

    // Optimistic update
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId
          ? { ...notification, is_read: true }
          : notification,
      ),
    );
  }, []);

  // Function to mark admin notification as read
  const markAdminNotificationAsRead = useCallback((notificationId) => {
    if (socketService.isUserAdmin()) {
      socketService.sendAdminCommand({
        command: "mark_as_read",
        id: notificationId,
      });

      // Optimistic update
      setAdminNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, is_read: true }
            : notification,
        ),
      );

      // Decrement unread count
      setAdminUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  // Function to mark all notifications as read
  const markAllAsRead = useCallback(() => {
    socketService.markAllAsRead();

    // Optimistic update
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, is_read: true })),
    );
    setUnreadCount(0);
  }, []);

  // Function to mark all admin notifications as read
  const markAllAdminNotificationsAsRead = useCallback(() => {
    if (socketService.isUserAdmin()) {
      socketService.sendAdminCommand({
        command: "mark_all_read",
      });

      // Optimistic update
      setAdminNotifications((prev) =>
        prev.map((notification) => ({ ...notification, is_read: true })),
      );
      setAdminUnreadCount(0);
    }
  }, []);

  // Function to refresh notifications
  const refreshNotifications = useCallback(() => {
    // This is a manual refresh - don't play sound
    setShouldPlaySound(false);
    socketService.getNotifications();
  }, []);

  // Function to refresh admin notifications
  const refreshAdminNotifications = useCallback(() => {
    if (socketService.isUserAdmin()) {
      // This is a manual refresh - don't play sound
      setShouldPlaySound(false);
      socketService.sendAdminCommand({
        command: "get_notifications",
      });
    }
  }, []);

  // Function to reset shouldPlaySound flag (called after sound is played)
  const resetShouldPlaySound = useCallback(() => {
    setShouldPlaySound(false);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        adminNotifications,
        unreadCount,
        adminUnreadCount,
        isConnected,
        isAdmin,
        tokenExpiryTime,
        markAsRead,
        markAdminNotificationAsRead,
        markAllAsRead,
        markAllAdminNotificationsAsRead,
        refreshNotifications,
        refreshAdminNotifications,
        shouldPlaySound,
        resetShouldPlaySound,
      }}
    >
      {children}

      {/* Render the toast notification outside the normal component hierarchy */}
      {toastNotification && (
        <NotificationToast
          notification={toastNotification}
          onClose={clearToastNotification}
        />
      )}
    </NotificationContext.Provider>
  );
};

// Custom hook for using the notification context
export const useNotifications = () => useContext(NotificationContext);

export default NotificationContext;
